import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Analytics from '@lib/analytics';
import SelectList from '@components/application/input/selectList';

export default class Calculator extends Component {
  static propTypes = {
    authenticityToken: PropTypes.string,
    availableContractMileage: PropTypes.number,
    calculatorChanged: PropTypes.func,
    dealTotal: PropTypes.number,
    equity: PropTypes.number,
    financeType: PropTypes.string,
    formAction: PropTypes.string,
    initialAnnualMileage: PropTypes.number,
    initialDeposit: PropTypes.number,
    initialTerm: PropTypes.number,
    isDepositSaved: PropTypes.bool,
    maxDeposit: PropTypes.number,
    mileageOptions: PropTypes.array,
    termOptions: PropTypes.array,
  };

  constructor(props) {
    super(props);

    this.state = {
      depositSaved: this.props.isDepositSaved,
      error: null,
      errorMessage: null,
      mileageOptions: this.newMileageOptions(this.props.initialTerm),
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.financeType !== prevProps.financeType) {
      this.setState({
        mileageOptions: this.props.mileageOptions,
      });
    }
  }

  findTermValue = () => {
    const termSelect = document.getElementById('term');
    return termSelect.options[termSelect.selectedIndex].text;
  };

  findMileageValue = () => {
    const mileageSelect = document.getElementById('mileage');
    return mileageSelect.options[mileageSelect.selectedIndex].text;
  };

  fireAnalyticsEvent = () => {
    const eventAction = this.state.depositSaved
      ? 'Saved deposit'
      : 'Deposit not saved';

    const finance = this.props.formAction === '#' ? 'PCP' : 'HP';

    Analytics.trackEvent('interaction', {
      action: `Calculator - ${eventAction} - ${finance}`,
      label: `£${this.deposit.value}`,
    });
  };

  onFormSubmit = (event) => {
    event.preventDefault();
    const total = this.props.dealTotal;
    const deposit = parseFloat(this.deposit.value);
    const term = parseInt(this.findTermValue());
    const mileage = this.props.mileageOptions
      ? parseInt(this.findMileageValue())
      : '';

    if (this.depositValid(deposit)) {
      this.fireAnalyticsEvent();

      this.resetError();
      return this.props.calculatorChanged(
        term,
        deposit,
        total,
        mileage,
        this.state.depositSaved
      );
    }
    this.setError(deposit);
  };

  depositValid = (deposit) => {
    return (
      deposit <= this.props.maxDeposit &&
      deposit >= this.minDeposit() &&
      Number.isInteger(deposit)
    );
  };

  setError = (deposit) => {
    const maxDepositError = deposit > this.props.maxDeposit;
    const requirement = maxDepositError ? 'less than' : 'greater than';
    const limit = maxDepositError ? this.props.maxDeposit : this.minDeposit();
    const errorMessage = !Number.isInteger(deposit)
      ? 'Deposit must be a whole number'
      : `Value must be ${requirement} or equal to £${limit}`;
    this.setState({ error: true, errorMessage });
  };

  resetError = () => {
    this.setState({ error: null, errorMessage: null });
  };

  updateMileageOptions = (name, value) => {
    const term = parseInt(value);
    const mileageOptions = this.newMileageOptions(term);
    this.setState({ mileageOptions });
  };

  newMileageOptions = (term) => {
    if (this.props.mileageOptions) {
      const maxAnnualMilage = (this.props.availableContractMileage / term) * 12;
      return this.props.mileageOptions.filter(
        (option) => option[1] <= maxAnnualMilage
      );
    }
  };

  minDeposit = () => {
    return this.props.equity < 0 ? 0 - this.props.equity : 0;
  };

  defaultDeposit = () => {
    return this.props.initialDeposit < this.minDeposit()
      ? this.minDeposit()
      : this.props.initialDeposit;
  };

  toggleSavedDeposit = () => {
    this.setState((prevState) => ({
      depositSaved: !prevState.depositSaved,
    }));
  };

  render() {
    return (
      <form
        action={this.props.formAction}
        className="js-off--hide ch-form"
        method="POST"
        noValidate
        onSubmit={this.onFormSubmit}
      >
        <input name="utf8" type="hidden" value="✓" />
        <input
          name="authenticity_token"
          type="hidden"
          value={this.props.authenticityToken}
        />
        <div
          className={`ch-form__group ${
            this.state.error ? 'ch-form__group--error' : ''
          }`}
        >
          <label className="ch-fs--3" htmlFor="deposit">
            Deposit
          </label>
          {this.state.error && (
            <span className="ch-form__control-validation">
              {this.state.errorMessage}
            </span>
          )}
          <input
            className="ch-form__control"
            defaultValue={this.defaultDeposit()}
            id="deposit"
            inputMode="numeric"
            name="deposit"
            pattern="[0-9]*"
            ref={(deposit) => (this.deposit = deposit)}
            required
            type="text"
          />
        </div>
        <div className="ch-form__group">
          <label
            className={`ch-fs--3 ch-text--normal ch-color--grey-7 save-deposit-toggle ${
              this.state.depositSaved && 'save-deposit-toggle--checked'
            }`}
          >
            Remember my deposit
            <input
              className="ch-reader"
              id="save_deposit"
              onChange={this.toggleSavedDeposit}
              type="checkbox"
            />
            <span className="save-deposit-toggle__indicator">
              <span className="save-deposit-toggle__indicator-inner" />
            </span>
          </label>
        </div>
        <div className="ch-form__group">
          <label className="ch-fs--3" htmlFor="term">
            Term
          </label>
          <SelectList
            className="ch-form__control"
            defaultSelectedOption={this.props.initialTerm.toString()}
            id="term"
            name="term"
            onChange={this.updateMileageOptions}
            optionsForSelect={this.props.termOptions}
          />
        </div>
        {this.props.mileageOptions && (
          <div className="ch-form__group">
            <label className="ch-fs--3" htmlFor="mileage">
              Annual mileage
            </label>
            <SelectList
              className="ch-form__control"
              defaultSelectedOption={this.props.initialAnnualMileage.toString()}
              id="mileage"
              name="mileage"
              optionsForSelect={this.state.mileageOptions}
            />
          </div>
        )}
        <div className="ch-form__group">
          <button
            className="ch-btn ch-btn--primary ch-btn--block"
            type="submit"
          >
            Calculate your finance
          </button>
        </div>
      </form>
    );
  }
}
